"use client";

import React, { useState, useEffect, useMemo } from "react";
import Image from "next/image";

type ResponsiveValue<T> =
  | T
  | {
      base?: T;
      sm?: T;
      md?: T;
      lg?: T;
      xl?: T;
      "2xl"?: T;
    };

interface FloatingArrowProps {
  /**
   * Arrow image source
   */
  src: string;
  /**
   * Position - top value (CSS value: px, %, etc.) or responsive object
   */
  top?: ResponsiveValue<string | number>;
  /**
   * Position - right value (CSS value: px, %, etc.) or responsive object
   */
  right?: ResponsiveValue<string | number>;
  /**
   * Position - bottom value (CSS value: px, %, etc.) or responsive object
   */
  bottom?: ResponsiveValue<string | number>;
  /**
   * Position - left value (CSS value: px, %, etc.) or responsive object
   */
  left?: ResponsiveValue<string | number>;
  /**
   * Width of the arrow container or responsive object
   */
  width?: ResponsiveValue<string | number>;
  /**
   * Height of the arrow container or responsive object
   */
  height?: ResponsiveValue<string | number>;
  /**
   * Rotation in degrees (0-360) or responsive object
   */
  rotate?: ResponsiveValue<number>;
  /**
   * Flip horizontally or responsive object
   */
  flipX?: ResponsiveValue<boolean>;
  /**
   * Flip vertically or responsive object
   */
  flipY?: ResponsiveValue<boolean>;
  /**
   * Z-index for layering or responsive object
   */
  zIndex?: ResponsiveValue<number>;
  /**
   * Additional className
   */
  className?: string;
  /**
   * Transform origin (default: "center center")
   */
  transformOrigin?: string;
  /**
   * Set to true for above-the-fold / LCP images (enables loading="eager")
   */
  priority?: boolean;
}

// Helper function to get responsive value based on breakpoint
const getResponsiveValue = <T,>(
  value: ResponsiveValue<T> | undefined,
  defaultValue: T,
  breakpoint: "base" | "sm" | "md" | "lg" | "xl" | "2xl"
): T => {
  if (value === undefined) return defaultValue;

  // If it's not an object, return the value directly
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return value as T;
  }

  const responsiveObj = value as any;

  // For each breakpoint, check the current breakpoint first, then fall back to smaller ones
  if (breakpoint === "2xl") {
    return (
      responsiveObj["2xl"] ??
      responsiveObj.xl ??
      responsiveObj.lg ??
      responsiveObj.md ??
      responsiveObj.sm ??
      responsiveObj.base ??
      defaultValue
    );
  }

  if (breakpoint === "xl") {
    return (
      responsiveObj.xl ??
      responsiveObj.lg ??
      responsiveObj.md ??
      responsiveObj.sm ??
      responsiveObj.base ??
      defaultValue
    );
  }

  if (breakpoint === "lg") {
    return (
      responsiveObj.lg ??
      responsiveObj.md ??
      responsiveObj.sm ??
      responsiveObj.base ??
      defaultValue
    );
  }

  if (breakpoint === "md") {
    return (
      responsiveObj.md ?? responsiveObj.sm ?? responsiveObj.base ?? defaultValue
    );
  }

  if (breakpoint === "sm") {
    return responsiveObj.sm ?? responsiveObj.base ?? defaultValue;
  }

  // base breakpoint
  return responsiveObj.base ?? defaultValue;
};

// Hook to get current breakpoint
const useBreakpoint = () => {
  const [breakpoint, setBreakpoint] = useState<
    "base" | "sm" | "md" | "lg" | "xl" | "2xl"
  >("base");

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      // Check from largest to smallest to get the correct breakpoint
      if (width >= 1536) {
        setBreakpoint("2xl");
      } else if (width >= 1280) {
        setBreakpoint("xl");
      } else if (width >= 1024) {
        setBreakpoint("lg");
      } else if (width >= 768) {
        setBreakpoint("md");
      } else if (width >= 640) {
        setBreakpoint("sm");
      } else {
        setBreakpoint("base");
      }
    };

    // Set initial breakpoint
    if (typeof window !== "undefined") {
      updateBreakpoint();
      window.addEventListener("resize", updateBreakpoint);
      return () => window.removeEventListener("resize", updateBreakpoint);
    }
  }, []);

  return breakpoint;
};

const FloatingArrow: React.FC<FloatingArrowProps> = ({
  src,
  top,
  right,
  bottom,
  left,
  width = "100px",
  height = "50px",
  rotate = 0,
  flipX = false,
  flipY = false,
  zIndex = 0,
  className = "",
  transformOrigin = "center center",
  priority = false,
}) => {
  const breakpoint = useBreakpoint();

  // Get current responsive values - use useMemo to ensure they update when breakpoint changes
  const currentTop = useMemo(
    () => getResponsiveValue(top, undefined, breakpoint),
    [top, breakpoint]
  );
  const currentRight = useMemo(
    () => getResponsiveValue(right, undefined, breakpoint),
    [right, breakpoint]
  );
  const currentBottom = useMemo(
    () => getResponsiveValue(bottom, undefined, breakpoint),
    [bottom, breakpoint]
  );
  const currentLeft = useMemo(
    () => getResponsiveValue(left, undefined, breakpoint),
    [left, breakpoint]
  );
  const currentWidth = useMemo(
    () => getResponsiveValue(width, "100px", breakpoint),
    [width, breakpoint]
  );
  const currentHeight = useMemo(
    () => getResponsiveValue(height, "50px", breakpoint),
    [height, breakpoint]
  );
  const currentRotate = useMemo(
    () => getResponsiveValue(rotate, 0, breakpoint),
    [rotate, breakpoint]
  );
  const currentFlipX = useMemo(
    () => getResponsiveValue(flipX, false, breakpoint),
    [flipX, breakpoint]
  );
  const currentFlipY = useMemo(
    () => getResponsiveValue(flipY, false, breakpoint),
    [flipY, breakpoint]
  );
  const currentZIndex = useMemo(
    () => getResponsiveValue(zIndex, 0, breakpoint),
    [zIndex, breakpoint]
  );

  // Calculate transform styles
  const transforms: string[] = [];

  if (currentRotate !== 0) {
    transforms.push(`rotate(${currentRotate}deg)`);
  }

  if (currentFlipX) {
    transforms.push("scaleX(-1)");
  }

  if (currentFlipY) {
    transforms.push("scaleY(-1)");
  }

  const transformStyle =
    transforms.length > 0 ? transforms.join(" ") : undefined;

  // Build position styles
  const positionStyle: React.CSSProperties = {
    position: "absolute",
    zIndex: currentZIndex,
    transformOrigin,
  };

  if (currentTop !== undefined) {
    positionStyle.top =
      typeof currentTop === "number" ? `${currentTop}px` : currentTop;
  }
  if (currentRight !== undefined) {
    positionStyle.right =
      typeof currentRight === "number" ? `${currentRight}px` : currentRight;
  }
  if (currentBottom !== undefined) {
    positionStyle.bottom =
      typeof currentBottom === "number" ? `${currentBottom}px` : currentBottom;
  }
  if (currentLeft !== undefined) {
    positionStyle.left =
      typeof currentLeft === "number" ? `${currentLeft}px` : currentLeft;
  }

  const widthValue =
    typeof currentWidth === "number" ? `${currentWidth}px` : currentWidth;
  const heightValue =
    typeof currentHeight === "number" ? `${currentHeight}px` : currentHeight;

  // Force re-render when breakpoint changes by using breakpoint in key
  return (
    <div
      key={`arrow-${breakpoint}`}
      className={`relative hidden md:block ${className}`}
      style={{
        ...positionStyle,
        width: widthValue,
        height: heightValue,
      }}
    >
      <div
        style={{
          position: "relative",
          transform: transformStyle,
          transformOrigin,
          width: "100%",
          height: "100%",
        }}
      >
        <Image
          src={src}
          alt="Arrow"
          fill
          className="object-contain"
          sizes={`${widthValue} ${heightValue}`}
          priority={priority}
        />
      </div>
    </div>
  );
};

export default FloatingArrow;
